import { useCallback } from 'react';

import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';

import { IRestaurant } from '@rbi-ctg/store';
import { useServiceModeContext } from 'state/service-mode';

import { useGetStoreFn } from './use-get-store-fn';

/**
 * useRestaurantStatusChecker - A custom React hook to periodically check the
 * availability status of a restaurant.
 *
 * On non-web platforms, the hook returns the same structure than the one
 * returned on web without including the web-specific logic.
 */
export function useRestaurantStatusChecker() {
  const { serviceMode } = useServiceModeContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();
  const getStore = useGetStoreFn();

  const isRestaurantAvailable = useCallback(
    async (store: IRestaurant) => {
      const fetchedStore = await getStore(store.number ?? null);
      if (!fetchedStore) {
        return false;
      }

      // Note that the store fetched from this method lacks some properties needed
      // to properly calculate availability this is why we need to merge the result with the existing one
      // missing properties: hasMobileOrdering and mobileOrderingStatus
      // TODO: investigate if this is needed to determine availability
      const updatedStore = { ...store, ...fetchedStore };
      return isStoreAvailable(updatedStore as IRestaurant, serviceMode);
    },
    [getStore, isStoreAvailable, serviceMode]
  );

  return {
    isRestaurantAvailable,
  };
}
